import AddIcon from "@mui/icons-material/Add";
import { FacilityPhotos } from "./facility-photos";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ALL_SERVICES,
  getCancellationPolicy,
  getFacility,
  getFacilityWaiver,
  publishFacilityWaiver,
  setCancellationPolicy,
  setPlayerAllocation,
  updateFacility,
  type CancellationWindow,
  type FacilityDetail,
  type WaiverVersion,
} from "@/libs/facilityApi";
import { checkFacilityPermission } from "@/libs/permissionsApi";
import { asRatePercent } from "@bw/shared/types";
import { parseDecimal } from "@bw/shared/utils";
import {
  FacilityIdentityEditor,
  type FacilityIdentityPersistenceState,
} from "@/components/address/FacilityIdentityEditor";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  FacilityApprovalPanel,
  facilityApprovalReadinessQueryKey,
} from "./facility-approval";
import { OperatingHoursPanel } from "./operating-hours-panel";

// Local edit-state shape for a cancellation window. The number inputs bind to
// plain numbers; the wire shape (CancellationWindow, fee_percentage: RatePercent)
// is reconstructed only at save. Keeping form state separate from wire data is
// why the branded API type does not bleed into useState (ADR-005).
interface PolicyWindowForm {
  hours_before: number;
  fee_percentage: number;
}

const SERVICE_LABELS: Record<string, string> = {
  court_reservation: "Court Reservations",
  open_play: "Drop-in",
  memberships: "Memberships",
  coaching: "Coaching",
  classes: "Classes",
};

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: { xs: 0.5, sm: 2 },
        gridTemplateColumns: {
          xs: "1fr",
          sm: "minmax(8rem, auto) minmax(0, 1fr)",
        },
        py: 1.25,
      }}
    >
      <Typography color="text.secondary" component="dt" variant="body2">
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{
          fontWeight: 700,
          m: 0,
          minWidth: 0,
          overflowWrap: "anywhere",
          textAlign: { xs: "left", sm: "right" },
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

function SettingsSection({
  children,
  id,
  title,
}: {
  children: React.ReactNode;
  id?: string;
  title: string;
}) {
  return (
    <Paper
      id={id}
      variant="outlined"
      sx={{ overflow: "hidden", scrollMarginTop: 80 }}
    >
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>{children}</Box>
    </Paper>
  );
}

function normalizeCachedFacility(detail: FacilityDetail): FacilityDetail {
  return detail.enabled_services ? detail : { ...detail, enabled_services: [] };
}

export function FacilitySettings() {
  const queryClient = useQueryClient();
  const { facilityId, isLoading: facilityLoading } = useResolvedFacility();
  const selectedFacilityIdRef = useRef(facilityId);
  selectedFacilityIdRef.current = facilityId;
  const [error, setError] = useState<string | null>(null);
  const [statusTarget, setStatusTarget] = useState<"active" | "disabled" | null>(
    null,
  );
  const [statusNotice, setStatusNotice] = useState<string | null>(null);
  const inFlightKeysRef = useRef<ReadonlySet<string>>(new Set());
  const [inFlightKeys, setInFlightKeys] = useState<ReadonlySet<string>>(
    inFlightKeysRef.current,
  );
  const facilityQuery = useQuery({
    queryKey: ["facility", facilityId],
    queryFn: () => getFacility(facilityId as string),
    enabled: Boolean(facilityId),
    select: normalizeCachedFacility,
  });
  const facility = facilityQuery.data ?? null;

  // Cancellation policy
  const [windows, setWindows] = useState<PolicyWindowForm[]>([]);
  const [policyDirty, setPolicyDirty] = useState(false);
  const [policyNotice, setPolicyNotice] = useState<string | null>(null);

  // Waiver
  const [waiver, setWaiver] = useState<WaiverVersion | null>(null);
  const [waiverText, setWaiverText] = useState("");
  const [waiverNotice, setWaiverNotice] = useState<string | null>(null);
  const [configFacilityId, setConfigFacilityId] = useState<string | null>(null);
  const [configLoadError, setConfigLoadError] = useState(false);
  const [configLoadAttempt, setConfigLoadAttempt] = useState(0);
  const [identityPersistence, setIdentityPersistence] = useState<
    FacilityIdentityPersistenceState & { facilityId: string | null }
  >({ facilityId: null, dirty: false, saving: false });

  // Player allocation — per-court cap toggle. Seeded from
  // facility.per_court_allocation on load; persisted only on explicit Save
  // (no auto-save on toggle). canManage is the SERVER's facility:manage answer,
  // never derived from the user object client-side.
  const [allocationEnabled, setAllocationEnabled] = useState(false);
  const [allocationMaxCount, setAllocationMaxCount] = useState<number | null>(
    null,
  );
  const [allocationDirty, setAllocationDirty] = useState(false);
  const [allocationNotice, setAllocationNotice] = useState<string | null>(null);
  const [allocationRangeError, setAllocationRangeError] = useState<
    string | null
  >(null);

  function requestKey(resource: string, requestFacilityId: string): string {
    return `${requestFacilityId}:${resource}`;
  }

  function beginRequest(key: string): boolean {
    if (inFlightKeysRef.current.has(key)) return false;
    const next = new Set(inFlightKeysRef.current);
    next.add(key);
    inFlightKeysRef.current = next;
    setInFlightKeys(next);
    return true;
  }

  function endRequest(key: string) {
    if (!inFlightKeysRef.current.has(key)) return;
    const next = new Set(inFlightKeysRef.current);
    next.delete(key);
    inFlightKeysRef.current = next;
    setInFlightKeys(next);
  }

  const configWritePending = facility
    ? ["policy", "waiver"].some((resource) =>
        inFlightKeys.has(requestKey(resource, facility.facility_id)),
      )
    : false;

  // Clear every facility-scoped form before the newly selected facility paints.
  // Without this reset, an operator can briefly see and submit another
  // facility's hours, cancellation policy, waiver, or allocation settings.
  useLayoutEffect(() => {
    const detail = facilityQuery.data;
    if (!detail) return;
    setError(null);
    setStatusTarget(null);
    setStatusNotice(null);
    setWindows([]);
    setPolicyDirty(false);
    setPolicyNotice(null);
    setWaiver(null);
    setWaiverText("");
    setWaiverNotice(null);
    setConfigFacilityId(null);
    setConfigLoadError(false);
    setConfigLoadAttempt(0);
    setIdentityPersistence({
      facilityId: detail.facility_id,
      dirty: false,
      saving: false,
    });

    const alloc = detail.per_court_allocation;
    setAllocationEnabled(alloc?.enabled ?? false);
    setAllocationMaxCount(alloc?.max_count ?? null);
    setAllocationDirty(false);
    setAllocationNotice(null);
    setAllocationRangeError(null);
  }, [facilityQuery.data?.facility_id]);

  useEffect(() => {
    const detail = facilityQuery.data;
    if (
      !detail ||
      configWritePending ||
      configFacilityId === detail.facility_id
    ) {
      return;
    }
    let cancelled = false;
    // Load go-live preflight data in parallel; tolerate missing config.
    void Promise.allSettled([
      getCancellationPolicy(detail.facility_id),
      getFacilityWaiver(detail.facility_id),
    ]).then(([policyRes, waiverRes]) => {
      if (cancelled || selectedFacilityIdRef.current !== detail.facility_id) {
        return;
      }
      if (policyRes.status === "rejected" || waiverRes.status === "rejected") {
        setConfigLoadError(true);
        return;
      }
      setWindows(
        policyRes.value.windows.map((w) => ({
          hours_before: Number(w.hours_before),
          fee_percentage: parseDecimal(w.fee_percentage) ?? 0,
        })),
      );
      setWaiver(waiverRes.value);
      setWaiverText(waiverRes.value?.content ?? "");
      setConfigLoadError(false);
      setConfigFacilityId(detail.facility_id);
    });
    return () => {
      cancelled = true;
    };
  }, [
    configLoadAttempt,
    configFacilityId,
    configWritePending,
    facilityQuery.data?.facility_id,
  ]);

  // Server-computed permission is server state. Transport failures remain
  // distinct from a real denial and keep every managed control fail-closed.
  const permissionQuery = useQuery({
    queryKey: ["facility-permission", facilityId, "facility:manage"],
    queryFn: () =>
      checkFacilityPermission(facilityId as string, "facility:manage"),
    enabled: Boolean(facilityId),
    retry: false,
  });
  const canManage = permissionQuery.data?.allowed === true;
  const permissionChecked =
    permissionQuery.isSuccess || permissionQuery.isError;
  const pricePermissionQuery = useQuery({
    queryKey: ["facility-permission", facilityId, "facility:price"],
    queryFn: () =>
      checkFacilityPermission(facilityId as string, "facility:price"),
    enabled: Boolean(facilityId),
    retry: false,
  });
  const canPrice = pricePermissionQuery.data?.allowed === true;
  const pricePermissionChecked =
    pricePermissionQuery.isSuccess || pricePermissionQuery.isError;

  const toggleService = async (service: string, on: boolean) => {
    if (!facility || !canManage) return;
    const requestFacilityId = facility.facility_id;
    const key = requestKey("services", requestFacilityId);
    if (!beginRequest(key)) return;
    const next = on
      ? Array.from(new Set([...facility.enabled_services, service]))
      : facility.enabled_services.filter((s) => s !== service);
    const prev = facility;
    queryClient.setQueryData<FacilityDetail>(
      ["facility", facility.facility_id],
      { ...facility, enabled_services: next },
    );
    try {
      const updated = await updateFacility(requestFacilityId, {
        enabled_services: next,
      });
      queryClient.setQueryData(["facility", requestFacilityId], updated);
      void queryClient.invalidateQueries({
        queryKey: facilityApprovalReadinessQueryKey(requestFacilityId),
      });
    } catch (err) {
      queryClient.setQueryData(["facility", requestFacilityId], prev);
      if (selectedFacilityIdRef.current === requestFacilityId) {
        setError(
          err instanceof Error ? err.message : "Failed to update services",
        );
      }
    } finally {
      endRequest(key);
    }
  };

  const changeFacilityStatus = async () => {
    if (!facility || !canManage || statusTarget == null) return;
    const requestFacilityId = facility.facility_id;
    const nextStatus = statusTarget;
    const key = requestKey("status", requestFacilityId);
    if (!beginRequest(key)) return;
    setError(null);
    setStatusNotice(null);
    try {
      const updated = await updateFacility(requestFacilityId, {
        status: nextStatus,
      });
      queryClient.setQueryData(
        ["facility", requestFacilityId],
        normalizeCachedFacility(updated),
      );
      void queryClient.invalidateQueries({
        queryKey: ["org-facilities", facility.organization_id],
      });
      void queryClient.invalidateQueries({
        queryKey: facilityApprovalReadinessQueryKey(requestFacilityId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["facility-discovery-readiness", requestFacilityId],
      });
      if (selectedFacilityIdRef.current === requestFacilityId) {
        setStatusTarget(null);
        setStatusNotice(
          nextStatus === "active"
            ? "Facility activated. Player visibility still follows approval and service readiness."
            : "Facility disabled. New player discovery and bookings are paused.",
        );
      }
    } catch (err) {
      if (selectedFacilityIdRef.current === requestFacilityId) {
        setError(
          err instanceof Error
            ? err.message
            : `Failed to ${nextStatus === "active" ? "activate" : "disable"} facility`,
        );
      }
    } finally {
      endRequest(key);
    }
  };

  const savePolicy = async () => {
    if (!facility || !canPrice || configFacilityId !== facility.facility_id) {
      return;
    }
    const requestFacilityId = facility.facility_id;
    const key = requestKey("policy", requestFacilityId);
    if (!beginRequest(key)) return;
    setPolicyNotice(null);
    setError(null);
    try {
      // Build the wire shape: fee_percentage is a branded RatePercent (the form
      // holds a plain number). asRatePercent brands the canonical Decimal string.
      const payload: CancellationWindow[] = windows.map((w) => ({
        hours_before: Number(w.hours_before),
        fee_percentage: asRatePercent(String(Number(w.fee_percentage)))!,
      }));
      const saved = await setCancellationPolicy(requestFacilityId, payload);
      if (selectedFacilityIdRef.current !== requestFacilityId) return;
      setWindows(
        saved.windows.map((w) => ({
          hours_before: Number(w.hours_before),
          fee_percentage: parseDecimal(w.fee_percentage) ?? 0,
        })),
      );
      setPolicyDirty(false);
      setPolicyNotice("Cancellation policy saved.");
    } catch (err) {
      if (selectedFacilityIdRef.current === requestFacilityId) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to save cancellation policy",
        );
      }
    } finally {
      endRequest(key);
    }
  };

  const publishWaiver = async () => {
    if (!facility || !canManage || configFacilityId !== facility.facility_id) {
      return;
    }
    const requestFacilityId = facility.facility_id;
    const key = requestKey("waiver", requestFacilityId);
    if (!beginRequest(key)) return;
    setWaiverNotice(null);
    setError(null);
    try {
      const published = await publishFacilityWaiver(
        requestFacilityId,
        waiverText,
      );
      if (selectedFacilityIdRef.current !== requestFacilityId) return;
      setWaiver(published);
      setWaiverText(published.content);
      setWaiverNotice(`Waiver version ${published.version_number} published.`);
    } catch (err) {
      if (selectedFacilityIdRef.current === requestFacilityId) {
        setError(
          err instanceof Error ? err.message : "Failed to publish waiver",
        );
      }
    } finally {
      endRequest(key);
    }
  };

  const saveAllocation = async () => {
    if (!facility || !canManage) return;
    const requestFacilityId = facility.facility_id;
    const key = requestKey("allocation", requestFacilityId);
    if (!beginRequest(key)) return;
    setAllocationNotice(null);
    setAllocationRangeError(null);
    // Client-side guard mirrors the server's integer 1–50 constraint so an
    // out-of-range OR fractional cap is caught before the request (the backend
    // field is int-typed, so a float like 2.5 would 422). Server is still
    // authoritative. Only enforced when allocation is enabled.
    if (
      allocationEnabled &&
      (allocationMaxCount == null ||
        !Number.isInteger(allocationMaxCount) ||
        allocationMaxCount < 1 ||
        allocationMaxCount > 50)
    ) {
      setAllocationRangeError(
        "Max players must be a whole number between 1 and 50.",
      );
      endRequest(key);
      return;
    }
    try {
      const saved = await setPlayerAllocation(requestFacilityId, {
        enabled: allocationEnabled,
        max_count: allocationEnabled ? allocationMaxCount : null,
      });
      if (selectedFacilityIdRef.current !== requestFacilityId) return;
      setAllocationEnabled(saved.enabled);
      setAllocationMaxCount(saved.max_count ?? null);
      setAllocationDirty(false);
      setAllocationNotice("Player allocation saved.");
      setError(null);
    } catch (err) {
      if (selectedFacilityIdRef.current === requestFacilityId) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to save player allocation",
        );
      }
    } finally {
      endRequest(key);
    }
  };

  if (facilityQuery.isLoading || facilityLoading || !facilityId) {
    return (
      <Box
        role="status"
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress aria-label="Loading facility settings" size={28} />
      </Box>
    );
  }

  if (facilityQuery.error && !facility) {
    return (
      <Alert severity="error" sx={{ mx: { xs: 2, lg: 3 } }}>
        {facilityQuery.error instanceof Error
          ? facilityQuery.error.message
          : "Failed to load facility"}
      </Alert>
    );
  }
  if (!facility) {
    return (
      <Alert severity="error" sx={{ mx: { xs: 2, lg: 3 } }}>
        No facility found.
      </Alert>
    );
  }

  const addr = facility.address;
  const identityRepairable =
    facility.approval_status === "draft" ||
    facility.approval_status === "rejected";
  const identityEditable = identityRepairable && permissionChecked && canManage;
  const identityPersistencePending =
    identityPersistence.facilityId === facility.facility_id &&
    (identityPersistence.dirty || identityPersistence.saving);
  const configReady = configFacilityId === facility.facility_id;
  const servicesSaving = inFlightKeys.has(
    requestKey("services", facility.facility_id),
  );
  const statusSaving = inFlightKeys.has(
    requestKey("status", facility.facility_id),
  );
  const policySaving = inFlightKeys.has(
    requestKey("policy", facility.facility_id),
  );
  const waiverPublishing = inFlightKeys.has(
    requestKey("waiver", facility.facility_id),
  );
  const allocationSaving = inFlightKeys.has(
    requestKey("allocation", facility.facility_id),
  );
  const waiverTooShort = waiverText.trim().length < 50;
  const waiverUnchanged =
    waiver != null && waiverText.trim() === waiver.content.trim();

  return (
    <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 }, pb: 4 }}>
      <Box>
        <Typography component="h1" variant="h5" sx={{ fontWeight: 800 }}>
          Facility Settings
        </Typography>
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ overflowWrap: "anywhere" }}
        >
          {facility.name}
        </Typography>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <FacilityApprovalPanel
        facility={facility}
        submissionAllowed={canManage}
        submissionBlocked={identityPersistencePending}
      />

      <FacilityPhotos
        key={facility.facility_id}
        facilityId={facility.facility_id}
        canManage={canManage}
      />
      <SettingsSection title="Identity">
        {identityRepairable && !permissionChecked ? (
          <Stack
            direction="row"
            role="status"
            spacing={1}
            sx={{ alignItems: "center" }}
          >
            <CircularProgress size={18} />
            <Typography color="text.secondary" variant="body2">
              Checking permission...
            </Typography>
          </Stack>
        ) : identityEditable ? (
          <FacilityIdentityEditor
            organizationId={facility.organization_id}
            facility={facility}
            onPersistenceStateChange={(state) =>
              setIdentityPersistence({
                facilityId: facility.facility_id,
                ...state,
              })
            }
            onSaved={(result) => {
              if (result.kind !== "updated") return;
              const saved = result.facility;
              queryClient.setQueryData(["facility", saved.facility_id], saved);
              void queryClient.invalidateQueries({
                queryKey: ["org-facilities", facility.organization_id],
              });
              void queryClient.invalidateQueries({
                queryKey: facilityApprovalReadinessQueryKey(saved.facility_id),
              });
            }}
          />
        ) : (
          <Stack spacing={2}>
            {identityRepairable &&
            permissionChecked &&
            !canManage &&
            !permissionQuery.isError ? (
              <Typography color="text.secondary" variant="body2">
                You need the Edit facility settings permission to change
                identity.
              </Typography>
            ) : null}
            {identityRepairable && permissionQuery.isError ? (
              <Alert
                action={
                  <Button
                    color="inherit"
                    onClick={() => void permissionQuery.refetch()}
                    size="small"
                    variant="outlined"
                  >
                    Retry permission check
                  </Button>
                }
                severity="error"
              >
                We couldn&apos;t verify your permission.
              </Alert>
            ) : null}
            <Stack component="dl" divider={<Divider flexItem />} sx={{ m: 0 }}>
              <Row label="Name" value={facility.name} />
              <Row label="Type" value={facility.facility_type} />
              <Row label="Timezone" value={facility.timezone} />
              <Row
                label="Address"
                value={[addr?.line1, addr?.city, addr?.state, addr?.postal_code]
                  .filter(Boolean)
                  .join(", ")}
              />
              <Row
                label="Status"
                value={
                  facility.status ? (
                    <Chip
                      label={facility.status}
                      size="small"
                      sx={{ textTransform: "capitalize" }}
                    />
                  ) : null
                }
              />
            </Stack>
          </Stack>
        )}
        {facility.approval_status === "approved" ? (
          <Stack spacing={1.25} sx={{ mt: 2 }}>
            <Typography color="text.secondary" variant="body2">
              {facility.status === "active"
                ? "Active facilities can appear in player discovery when each service is ready."
                : "Disabled facilities stay editable but are hidden from player discovery and new bookings."}
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: { sm: "center" } }}
            >
              <Button
                color={facility.status === "active" ? "warning" : "primary"}
                disabled={!canManage || statusSaving}
                onClick={() =>
                  setStatusTarget(
                    facility.status === "active" ? "disabled" : "active",
                  )
                }
                size="small"
                variant="outlined"
              >
                {statusSaving
                  ? "Saving…"
                  : facility.status === "active"
                    ? "Disable facility"
                    : "Activate facility"}
              </Button>
              {statusNotice ? (
                <Typography
                  color="text.secondary"
                  role="status"
                  variant="body2"
                >
                  {statusNotice}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        ) : null}
      </SettingsSection>

      <SettingsSection title="Services offered">
        <Stack divider={<Divider flexItem />}>
          {ALL_SERVICES.map((service) => (
            <Box
              key={service}
              sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "space-between",
                minHeight: 48,
                py: 0.5,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {SERVICE_LABELS[service] ?? service}
              </Typography>
              <Switch
                checked={facility.enabled_services.includes(service)}
                disabled={!canManage || servicesSaving}
                onChange={(_event, checked) =>
                  void toggleService(service, checked)
                }
                size="small"
                slotProps={{
                  input: {
                    "aria-label": `Offer ${SERVICE_LABELS[service] ?? service}`,
                  },
                }}
              />
            </Box>
          ))}
        </Stack>
      </SettingsSection>

      <SettingsSection id="player-allocation" title="Player allocation">
        <Stack spacing={2}>
          <Typography color="text.secondary" variant="body2">
            Cap how many players can join a single court for drop-in. The
            per-player surcharge itself is set in your pricing rules — this only
            enforces the headcount.
          </Typography>
          {permissionChecked && !canManage && !permissionQuery.isError ? (
            <Typography color="text.secondary" variant="body2">
              You need the Edit facility settings permission to change this.
            </Typography>
          ) : null}
          {permissionQuery.isError && !identityRepairable ? (
            <Alert
              action={
                <Button
                  color="inherit"
                  onClick={() => void permissionQuery.refetch()}
                  size="small"
                  variant="outlined"
                >
                  Retry permission check
                </Button>
              }
              severity="error"
            >
              We couldn&apos;t verify your permission.
            </Alert>
          ) : null}
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Switch
              checked={allocationEnabled}
              disabled={!canManage || allocationSaving}
              id="alloc-enable"
              onChange={(_event, checked) => {
                setAllocationEnabled(checked);
                setAllocationDirty(true);
                setAllocationRangeError(null);
              }}
              size="small"
              slotProps={{
                input: { "aria-labelledby": "alloc-enable-label" },
              }}
            />
            <Box
              component="label"
              htmlFor="alloc-enable"
              id="alloc-enable-label"
              sx={{ cursor: "pointer", fontSize: 14, fontWeight: 700 }}
            >
              Enable per-court allocation
            </Box>
          </Stack>
          {allocationEnabled ? (
            <Stack spacing={0.75} sx={{ alignItems: "flex-start" }}>
              <TextField
                disabled={!canManage || allocationSaving}
                error={Boolean(allocationRangeError)}
                id="alloc-max-players"
                label="Max players per court"
                onChange={(event) => {
                  const parsed =
                    event.target.value === ""
                      ? null
                      : Number(event.target.value);
                  setAllocationMaxCount(
                    parsed != null && Number.isNaN(parsed) ? null : parsed,
                  );
                  setAllocationDirty(true);
                  setAllocationRangeError(null);
                }}
                size="small"
                slotProps={{
                  htmlInput: {
                    "aria-describedby": allocationRangeError
                      ? "alloc-max-error"
                      : undefined,
                    max: 50,
                    min: 1,
                    step: 1,
                  },
                }}
                sx={{ width: 190 }}
                type="number"
                value={allocationMaxCount ?? ""}
              />
              {allocationRangeError ? (
                <Typography
                  color="error"
                  id="alloc-max-error"
                  role="alert"
                  variant="body2"
                >
                  {allocationRangeError}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <Button
              disabled={!canManage || allocationSaving || !allocationDirty}
              onClick={saveAllocation}
              size="small"
              variant="contained"
            >
              {allocationSaving ? "Saving…" : "Save allocation"}
            </Button>
            {allocationNotice ? (
              <Typography color="text.secondary" role="status" variant="body2">
                {allocationNotice}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </SettingsSection>

      <SettingsSection id="operating-hours" title="Operating hours">
        <OperatingHoursPanel
          canManage={canManage}
          facilityId={facility.facility_id}
        />
      </SettingsSection>

      {configReady ? (
        <>
          <SettingsSection id="cancellation-policy" title="Cancellation policy">
            <Stack spacing={2}>
              {!pricePermissionChecked ? (
                <Stack
                  direction="row"
                  role="status"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <CircularProgress size={18} />
                  <Typography color="text.secondary" variant="body2">
                    Checking pricing permission...
                  </Typography>
                </Stack>
              ) : pricePermissionQuery.isError ? (
                <Alert
                  action={
                    <Button
                      color="inherit"
                      onClick={() => void pricePermissionQuery.refetch()}
                      size="small"
                      variant="outlined"
                    >
                      Retry pricing permission check
                    </Button>
                  }
                  severity="error"
                >
                  We couldn&apos;t verify your pricing permission.
                </Alert>
              ) : !canPrice ? (
                <Typography color="text.secondary" variant="body2">
                  You need the Set pricing rules permission to change this
                  policy.
                </Typography>
              ) : null}
              <Typography color="text.secondary" variant="body2">
                Define cancellation windows and the fee charged in each. A 0%
                fee means a full refund; 100% means no refund. Players see this
                before booking.
              </Typography>
              {windows.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No windows yet. Add one to define your policy.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {windows.map((window, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          alignItems: "center",
                          display: "grid",
                          gap: 1.5,
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "minmax(0, 1fr) minmax(0, 1fr) auto",
                          },
                        }}
                      >
                        <TextField
                          disabled={!canPrice || policySaving}
                          label="Hours before booking"
                          onChange={(event) => {
                            const value =
                              event.target.value === ""
                                ? 0
                                : Number(event.target.value);
                            setWindows((previous) =>
                              previous.map((current, currentIndex) =>
                                currentIndex === index
                                  ? { ...current, hours_before: value }
                                  : current,
                              ),
                            );
                            setPolicyDirty(true);
                          }}
                          size="small"
                          slotProps={{
                            htmlInput: {
                              "aria-label": `Cancellation window ${index + 1} hours before booking`,
                              min: 0,
                            },
                          }}
                          type="number"
                          value={window.hours_before}
                        />
                        <TextField
                          disabled={!canPrice || policySaving}
                          label="Cancellation fee (%)"
                          onChange={(event) => {
                            const value =
                              event.target.value === ""
                                ? 0
                                : Number(event.target.value);
                            setWindows((previous) =>
                              previous.map((current, currentIndex) =>
                                currentIndex === index
                                  ? { ...current, fee_percentage: value }
                                  : current,
                              ),
                            );
                            setPolicyDirty(true);
                          }}
                          size="small"
                          slotProps={{
                            htmlInput: {
                              "aria-label": `Cancellation window ${index + 1} fee percentage`,
                              max: 100,
                              min: 0,
                            },
                          }}
                          type="number"
                          value={window.fee_percentage}
                        />
                        <IconButton
                          aria-label="Remove window"
                          color="error"
                          disabled={!canPrice || policySaving}
                          onClick={() => {
                            setWindows((previous) =>
                              previous.filter(
                                (_current, currentIndex) =>
                                  currentIndex !== index,
                              ),
                            );
                            setPolicyDirty(true);
                          }}
                          size="small"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              )}
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center", flexWrap: "wrap" }}
              >
                <Button
                  disabled={!canPrice || policySaving}
                  onClick={() => {
                    setWindows((previous) => [
                      ...previous,
                      { hours_before: 24, fee_percentage: 0 },
                    ]);
                    setPolicyDirty(true);
                  }}
                  size="small"
                  startIcon={<AddIcon />}
                  variant="outlined"
                >
                  Add window
                </Button>
                <Button
                  disabled={!canPrice || policySaving || !policyDirty}
                  onClick={savePolicy}
                  size="small"
                  variant="contained"
                >
                  {policySaving ? "Saving…" : "Save policy"}
                </Button>
                {policyNotice ? (
                  <Typography
                    color="text.secondary"
                    role="status"
                    variant="body2"
                  >
                    {policyNotice}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </SettingsSection>

          <SettingsSection id="waiver" title="Liability waiver">
            <Stack spacing={2}>
              {waiver ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center", flexWrap: "wrap" }}
                >
                  <Chip
                    label={`Version ${waiver.version_number}`}
                    size="small"
                  />
                  <Typography color="text.secondary" variant="body2">
                    Published{" "}
                    {new Date(waiver.published_at).toLocaleDateString()}
                  </Typography>
                </Stack>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  No waiver published yet.
                </Typography>
              )}
              <TextField
                disabled={!canManage || waiverPublishing}
                fullWidth
                id="waiver-text"
                label="Waiver text"
                minRows={10}
                multiline
                onChange={(event) => setWaiverText(event.target.value)}
                placeholder="Enter your facility liability waiver text…"
                value={waiverText}
              />
              <Typography color="text.secondary" variant="caption">
                Minimum 50 characters. Publishing a new version requires all
                players to re-accept.
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center", flexWrap: "wrap" }}
              >
                <Button
                  disabled={
                    !canManage ||
                    waiverPublishing ||
                    waiverTooShort ||
                    waiverUnchanged
                  }
                  onClick={publishWaiver}
                  size="small"
                  variant="contained"
                >
                  {waiverPublishing
                    ? "Publishing…"
                    : waiver
                      ? "Publish new version"
                      : "Publish waiver"}
                </Button>
                {waiverNotice ? (
                  <Typography
                    color="text.secondary"
                    role="status"
                    variant="body2"
                  >
                    {waiverNotice}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </SettingsSection>
        </>
      ) : configLoadError ? (
        <Alert
          action={
            <Button
              color="inherit"
              onClick={() => {
                setConfigLoadError(false);
                setConfigLoadAttempt((attempt) => attempt + 1);
              }}
              size="small"
              variant="outlined"
            >
              Retry configuration
            </Button>
          }
          severity="error"
        >
          We couldn&apos;t load this facility&apos;s hours, cancellation policy,
          and waiver. Editing stays locked until the data loads.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack
            direction="row"
            role="status"
            spacing={1}
            sx={{ alignItems: "center" }}
          >
            <CircularProgress size={18} />
            <Typography color="text.secondary" variant="body2">
              Loading facility configuration...
            </Typography>
          </Stack>
        </Paper>
      )}

      <SettingsSection title="Drop-in">
        <Box component="dl" sx={{ m: 0 }}>
          <Row
            label="Max duration"
            value={
              facility.drop_in_max_duration_hours != null
                ? `${facility.drop_in_max_duration_hours} h`
                : null
            }
          />
        </Box>
      </SettingsSection>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => {
          if (!statusSaving) setStatusTarget(null);
        }}
        open={statusTarget != null}
      >
        <DialogTitle>
          {statusTarget === "active" ? "Activate facility?" : "Disable facility?"}
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" variant="body2">
            {statusTarget === "active"
              ? "This can make the facility visible to players immediately when approval and service readiness are complete."
              : "This pauses player discovery and new bookings. Existing paid obligations stay protected, and the server refuses an unsafe transition."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={statusSaving} onClick={() => setStatusTarget(null)}>
            Keep current status
          </Button>
          <Button
            color={statusTarget === "active" ? "primary" : "warning"}
            disabled={statusSaving}
            onClick={() => void changeFacilityStatus()}
            variant="contained"
          >
            {statusSaving
              ? "Saving…"
              : statusTarget === "active"
                ? "Activate facility"
                : "Disable facility"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
